import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './UserDashboard.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../services/api';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [applicationStatus, setApplicationStatus] = useState('not-submitted');
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [hasApplication, setHasApplication] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('user');
      
      if (!token || !user) {
        navigate('/login');
        return false;
      }
      
      try {
        setUserData(JSON.parse(user));
        return true;
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/login');
        return false;
      }
    };

    const fetchApplicationStatus = async () => {
      if (!checkAuth()) return;

      try {
        const response = await api.get('/application/status');
        const data = response.data;
        
        if (data.hasApplication) {
          setHasApplication(true);
          setApplicationStatus(data.status || 'submitted');
        } else {
          setHasApplication(false);
          setApplicationStatus('not-submitted');
        }
      } catch (error) {
        console.error('Error fetching application status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplicationStatus();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getStatusInfo = () => {
    switch(applicationStatus) {
      case 'submitted':
        return {
          title: 'Application Submitted',
          message: 'Your application has been successfully submitted. Our team will review it shortly.',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          color: '#3b82f6',
          bgColor: 'rgba(59, 130, 246, 0.15)'
        };
      case 'under-review':
        return {
          title: 'Under Review',
          message: 'Your application is currently being reviewed by our admissions team. We will contact you soon.',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          color: '#f59e0b',
          bgColor: 'rgba(245, 158, 11, 0.15)'
        };
      case 'contacted':
        return {
          title: 'Contacted by Counsellor',
          message: 'Our counsellor has reached out to you. Please check your email for further instructions.',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2"/>
              <line x1="9" y1="10" x2="15" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="9" y1="14" x2="13" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ),
          color: '#8b5cf6',
          bgColor: 'rgba(139, 92, 246, 0.15)'
        };
      case 'accepted':
        return {
          title: 'Application Accepted',
          message: 'Congratulations! Your application has been accepted. Welcome to Aviator1903 Pilot Training Program!',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>
          ),
          color: '#10b981',
          bgColor: 'rgba(16, 185, 129, 0.15)'
        };
      case 'rejected':
        return {
          title: 'Application Rejected',
          message: 'Unfortunately, your application was not successful at this time. You can reapply after 6 months.',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ),
          color: '#ef4444',
          bgColor: 'rgba(239, 68, 68, 0.15)'
        };
      case 'not-submitted':
      default:
        return {
          title: 'No Application Found',
          message: 'You haven\'t submitted an application yet. Click the button below to get started!',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
              <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="18" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="9" y1="15" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ),
          color: '#6b7280',
          bgColor: 'rgba(107, 114, 128, 0.15)'
        };
    }
  };

  const statusInfo = getStatusInfo();

  if (loading) {
    return (
      <div className="user-dashboard-page">
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 400px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}>
          <div style={{textAlign: 'center'}}>
            <div className="spinner" style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 20px',
              border: '4px solid rgba(245, 158, 11, 0.2)',
              borderTopColor: '#f59e0b',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p>Loading dashboard...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="user-dashboard-page">
      <Header />
      
      <section className="user-dashboard-section">
        <div className="container">
          <div className="dashboard-header">
            <div>
              <h3 className="dashboard-subtitle">STUDENT PORTAL</h3>
              <h1 className="dashboard-title">MY <span className="title-highlight">DASHBOARD</span></h1>
            </div>
            <button className="btn-logout" onClick={handleLogout}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Logout
            </button>
          </div>

          <div className="welcome-banner">
            <div className="welcome-content">
              <h2>Welcome Back, {userData?.name || 'Student'}!</h2>
              <p>Track your application status and stay updated with your pilot training journey.</p>
            </div>
            <div className="welcome-badge">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
              </svg>
            </div>
          </div>

          {!hasApplication && (
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '2px solid #f59e0b',
              borderRadius: '12px',
              padding: '30px',
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              <h3 style={{color: '#f59e0b', fontSize: '24px', marginBottom: '15px'}}>
                Complete Your Application
              </h3>
              <p style={{color: '#e5e7eb', marginBottom: '20px', fontSize: '16px'}}>
                You haven't submitted your application yet. Click the button below to fill out the application form and upload required documents.
              </p>
              <button 
                onClick={() => navigate('/application-form')}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: 'white',
                  padding: '14px 32px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                Fill Application Form
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          <div className="application-status-container">
            <div 
              className="status-card"
              style={{
                background: statusInfo.bgColor,
                borderColor: statusInfo.color
              }}
            >
              <div 
                className="status-icon"
                style={{ color: statusInfo.color }}
              >
                {statusInfo.icon}
              </div>
              
              <h2 className="status-title" style={{ color: statusInfo.color }}>
                {statusInfo.title}
              </h2>
              
              <p className="status-message">
                {statusInfo.message}
              </p>

              {applicationStatus === 'not-submitted' || applicationStatus === 'rejected' ? (
                <button 
                  className="btn-apply"
                  onClick={() => navigate('/application-form')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Fill Application Form
                </button>
              ) : null}

              {applicationStatus === 'accepted' && (
                <div className="next-steps-info">
                  <h3>What's Next?</h3>
                  <ul>
                    <li>Check your email for enrollment instructions</li>
                    <li>Complete the payment process</li>
                    <li>Prepare required documents</li>
                    <li>Attend the orientation session</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UserDashboard;
